import dynamic from 'next/dynamic'
import { ReactElement } from 'react'
import {
  Box,
  ChartContainer,
  CustomLinkChartProps,
  Datum,
  Flex,
  Heading,
  ReportKeyChartProps,
  Skeleton,
  Stack,
  Text
} from '~/design_system'
import { SadStates } from '~/components/SadStates'
import { GenericChartProps } from '~/design_system/components/charts/ChartTypes'
import { useMsg } from '~/common/localizationUtils'
import { useChartFormat, useFormattedTitle } from './format'

// nivo elements have to be dynamically imported because d3 went ESM only and nextjs doesn't like it
const ResponsivePie = dynamic(() => import('@nivo/pie').then(m => m.ResponsivePie), { ssr: false })

// Pie Supports 4 colors for now.
// Check with the UX team before adding more colors.
// May be you don't need a Pie, if you need more colors.
const COLORS = ['#3077cc', '#29BA9A', '#203341', '#FF7F0E']

const getTotal = (data: Datum[]) =>
  data.reduce((acc, { value }) => acc + (typeof value === 'string' ? parseInt(value as string) : value), 0)

export type SummaryProps = {
  data: Datum[]
}

const Summary = ({ data }: SummaryProps) => {
  const msg = useMsg()

  return (
    <>
      <Heading as={Text} lineHeight={1}>
        {useChartFormat('number')(getTotal(data))}
      </Heading>
      <Text>{msg('term.total', { count: 1 })}</Text>
    </>
  )
}

interface BasePieChartProps extends GenericChartProps {
  summary?: ({ data }: SummaryProps) => ReactElement<any>
}

interface CustomLinkPieChartProps extends BasePieChartProps, CustomLinkChartProps {}

interface ReportKeyPieChartProps extends BasePieChartProps, ReportKeyChartProps {}

export type PieChartProps = CustomLinkPieChartProps | ReportKeyPieChartProps

export const PieChart = ({
  data,
  reportKey,
  chartConfig,
  summary: SummaryComponent = Summary,
  isLoading = true,
  isFetching,
  isError,
  isEmpty,
  customReportUrl
}: PieChartProps) => {
  const { title, subTitle, format = 'number', colors = COLORS } = chartConfig
  const valueFormat = useChartFormat(format)

  return (
    <ChartContainer
      reportKey={reportKey}
      title={useFormattedTitle(title, format)}
      subTitle={subTitle}
      isLoading={isLoading}
      isFetching={isFetching}
      isError={isError}
      isEmpty={isEmpty}
      customReportUrl={customReportUrl}>
      <SadStates states={[{ when: isLoading, render: () => <PieChartSkeleton /> }]}>
        {data && (
          <Flex direction={{ base: 'column', sm: 'row', md: 'column', lg: 'row' }} height="full">
            <Box
              width={{ base: '80%', sm: '60%', md: '80%', lg: '60%' }}
              height={{ base: 'full', sm: '80%' }}
              position="relative"
              mx={{ base: 'auto', sm: 'initial', md: 'auto', lg: 'initial' }}
              my={{ sm: 'auto', md: 'initial', lg: 'auto' }}>
              <ResponsivePie
                data={data}
                innerRadius={0.7}
                colors={colors}
                enableArcLabels={false}
                enableArcLinkLabels={false}
                theme={{ tooltip: { chip: { borderRadius: '50%' } } }}
              />
              <Stack
                position="absolute"
                top={0}
                bottom={0}
                left={0}
                right={0}
                justifyContent="center"
                alignItems="center"
                spacing={0}
                pointerEvents="none">
                <SummaryComponent data={data} />
              </Stack>
            </Box>
            <Box position="relative">
              <Box
                position={{ sm: 'absolute', md: 'initial', lg: 'absolute' }}
                top={{ sm: '50%', md: '50%', lg: '50%' }}
                transform={{ sm: 'translateY(-50%)', md: 'translateY(0%)', lg: 'translateY(-50%)' }}>
                <CustomLegend data={data} colors={colors} formatValue={valueFormat} />
              </Box>
            </Box>
          </Flex>
        )}
      </SadStates>
    </ChartContainer>
  )
}

const ITEM_SPACING = 4
const ITEM_HEIGHT = 10
const LEGEND_WIDTH = 230

type CustomLegendProps = {
  data: Datum[]
  colors: string[]
  formatValue: (value: number | string) => string
}

const CustomLegend = ({ data, colors, formatValue }: CustomLegendProps) => {
  // Create a wrapper component to handle the legend with tooltips
  return (
    <Box position="relative" width={LEGEND_WIDTH}>
      {data.map((item, index) => {
        const fullLabel = `${item.label || item.id} (${formatValue(item.value)})`
        return (
          <Box
            /* eslint-disable-next-line react/no-array-index-key --
             * DO NOT COPY/PASTE THIS eslint-disable!  This is here
             * because we wanted to start enforcing the rule, but couldn't fix all
             * spots.  This is currently working so we will leave it as is for now.
             */
            key={`${item.id || item.label}-${index}`}
            height={ITEM_HEIGHT}
            mb={index < data.length - 1 ? ITEM_SPACING : 0}
            display="flex"
            alignItems="center">
            <Box width="24px" height="24px" borderRadius="full" bg={colors[index]} mr={2} />
            <Text flexWrap="wrap" fontSize="16px" width={`${LEGEND_WIDTH - 40}px`}>
              {fullLabel}
            </Text>
          </Box>
        )
      })}
    </Box>
  )
}

const PieChartSkeleton = () => {
  const size = { base: 200, sm: 300 }
  const innerSize = { base: '133px', sm: '200px' }
  return (
    <Flex height="full">
      <Box position="relative" height={size} width={size} mx="auto" alignSelf="center">
        <Skeleton w="full" h="full" borderRadius="full" />
        <Box
          w={innerSize}
          height={innerSize}
          borderRadius="full"
          bg="white"
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
        />
      </Box>
    </Flex>
  )
}
